<template>
  <v-card id="payment-callback-list">
    <v-card-title class="align-start">
      <span>Приём платежей</span>
      <v-spacer></v-spacer>
      <v-btn
        outlined
        small
        :loading="loading"
        @click="fetch()"
      >
        <v-icon
          size="18"
          class="me-2"
        >
          {{ icons.mdiRefresh }}
        </v-icon>
        <span>Обновить</span>
      </v-btn>
    </v-card-title>

    <v-card-subtitle class="pb-0">
      Колбэки банка так, как их принял отдельный сервис: тело каждого, попытки его
      разобрать и то, что из него вышло.
    </v-card-subtitle>

    <v-card-text class="pt-5">
      <div class="d-flex flex-wrap payment-callback-list__totals">
        <v-chip
          v-for="total in statusTotals"
          :key="total.status"
          small
          label
          :outlined="filters.status !== total.status"
          :color="total.color"
          class="v-chip-light-bg"
          :class="`${total.color}--text`"
          @click="toggleStatus(total.status)"
        >
          {{ total.text }}: {{ total.count }}
        </v-chip>
        <v-chip
          v-if="!statusTotals.length && loaded && !filtered"
          small
          label
          outlined
        >
          колбэков ещё не было
        </v-chip>
      </div>
    </v-card-text>

    <v-card-text class="d-flex align-center flex-wrap pb-0 payment-callback-list__filters">
      <v-select
        v-model="filters.restaurantIds"
        :items="restaurantDropDown"
        multiple
        chips
        small-chips
        deletable-chips
        dense
        outlined
        hide-details
        clearable
        label="Заведения"
        class="payment-callback-list__restaurants"
      >
        <template #prepend-item>
          <v-list-item
            ripple
            @mousedown.prevent
            @click="toggleAllRestaurants"
          >
            <v-list-item-action>
              <v-icon :color="filters.restaurantIds.length ? 'primary' : ''">
                {{ allRestaurantsIcon }}
              </v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Все заведения</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-2"></v-divider>
        </template>

        <template #selection="{ item, index }">
          <v-chip
            v-if="index < 2"
            small
          >
            {{ item.text }}
          </v-chip>
          <span
            v-else-if="index === 2"
            class="text--secondary text-caption align-self-center"
          >
            и ещё {{ filters.restaurantIds.length - 2 }}
          </span>
        </template>
      </v-select>

      <v-text-field
        v-model="filters.search"
        single-line
        dense
        outlined
        clearable
        hide-details
        placeholder="Поиск по телу колбэка или адресу..."
        class="payment-callback-list__search"
      ></v-text-field>

      <div
        v-if="filters.restaurantIds.length"
        class="text--secondary text-caption payment-callback-list__hint"
      >
        Колбэки отбираются по заказам заведения, поэтому те, что к заказу привязать не
        удалось, под отбор не попадают.
      </div>
    </v-card-text>

    <v-card-text>
      <v-alert
        v-if="loaded && !receiverAvailable"
        text
        color="warning"
      >
        Сервис приёма платежей не ответил. Пока он молчит, сказать, что к нему
        приходило, нельзя.
      </v-alert>

      <v-alert
        v-else-if="loaded && !callbacks.length"
        text
        color="info"
      >
        {{ filtered ? 'Под фильтр ничего не подошло.' : 'Колбэков ещё не приходило.' }}
      </v-alert>

      <v-expansion-panels
        v-else
        accordion
        multiple
      >
        <payment-callback-panel
          v-for="callback in callbacks"
          :key="callback.id"
          :callback="callback"
        ></payment-callback-panel>
      </v-expansion-panels>

      <div
        v-if="pageCount > 1"
        class="d-flex justify-center mt-5"
      >
        <v-pagination
          v-model="filters.pageNumber"
          :length="pageCount"
          total-visible="7"
        ></v-pagination>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  mdiCheckboxBlankOutline, mdiCloseBox, mdiMinusBox, mdiRefresh,
} from '@mdi/js'
import debounce from 'lodash/debounce'
import PaymentCallbackPanel from '@/views/payment/payment-callback-panel.vue'
import { callbackStatus } from '@/helpers/payments'

export default {
  name: 'PaymentCallbackList',
  components: { PaymentCallbackPanel },
  async mounted() {
    this.fetchRestaurants()
    await this.fetch()
  },
  data: () => ({
    icons: {
      mdiRefresh, mdiCloseBox, mdiMinusBox, mdiCheckboxBlankOutline,
    },
    filters: {
      status: '',
      search: '',
      restaurantIds: [],
      pageNumber: 1,
      pageSize: 25,
    },
  }),
  computed: {
    ...mapState('paymentCallbacks', {
      callbacks: 'list',
      totals: 'totals',
      totalCount: 'totalCount',
      receiverAvailable: 'receiverAvailable',
      loading: 'loading',
      loaded: 'loaded',
    }),
    ...mapGetters('restaurant', { restaurantDropDown: 'restaurantDropDown' }),

    allRestaurantsSelected() {
      return this.restaurantDropDown.length > 0
        && this.filters.restaurantIds.length === this.restaurantDropDown.length
    },

    allRestaurantsIcon() {
      if (this.allRestaurantsSelected) return this.icons.mdiCloseBox
      if (this.filters.restaurantIds.length) return this.icons.mdiMinusBox

      return this.icons.mdiCheckboxBlankOutline
    },

    filtered() {
      return Boolean(this.filters.status || this.filters.search
        || this.filters.restaurantIds.length)
    },

    statusTotals() {
      return this.totals.map(total => ({
        status: total.status,
        count: total.count,
        ...callbackStatus(total.status),
      }))
    },
    pageCount() {
      return Math.ceil(this.totalCount / this.filters.pageSize)
    },
  },
  watch: {
    'filters.search': debounce(function search() {
      this.resetAndFetch()
    }, 400),
    'filters.status': 'resetAndFetch',
    'filters.restaurantIds': 'resetAndFetch',
    'filters.pageNumber': 'fetch',
  },
  methods: {
    ...mapActions('paymentCallbacks', ['fetchList']),
    ...mapActions('restaurant', { fetchRestaurants: 'fetchDropDown' }),
    async fetch() {
      await this.fetchList(this.filters)
    },
    async resetAndFetch() {
      if (this.filters.pageNumber !== 1) {
        this.filters.pageNumber = 1

        return
      }
      await this.fetch()
    },
    toggleStatus(status) {
      this.filters.status = this.filters.status === status ? '' : status
    },

    toggleAllRestaurants() {
      this.filters.restaurantIds = this.allRestaurantsSelected
        ? []
        : this.restaurantDropDown.map(x => x.value)
    },
  },
}
</script>

<style lang="scss" scoped>
.payment-callback-list__totals {
  gap: 8px;
}

.payment-callback-list__filters {
  gap: 16px;
}

.payment-callback-list__restaurants {
  max-width: 420px;
}

.payment-callback-list__search {
  max-width: 420px;
}

.payment-callback-list__hint {
  flex: 1 0 100%;
}
</style>
